// MS218: shared helper for the category-aware "Baking" status label.
//
// The `orders.status` enum is universal across vendor types ("pending"
// → "confirmed" → "baking" → ... → "delivered"/"cancelled"), but the
// `baking` display label only reads right for cake vendors. Hampers
// should see "Packing", florists "Tying", everything else "Preparing".
//
// MS214 introduced this branching at the customer-facing Orders page;
// MS217 extended it to the vendor partner portal. Five files ended up
// with their own copy of the same `preparingLabel` switch — this
// module lifts the truth into one place so every surface stays in
// sync.
//
// DB enum (`orders.vendor_category` CHECK constraint, see
// 20260517140000_ms216_add_vendor_category_to_orders.sql + MS278):
//   cake / hamper / florist / flowers / workshop / spa / experience /
//   meal / voucher / coffee
//
// We accept `flowers` as an alias for `florist` for legacy data and
// fall back to "Preparing" for anything not explicitly mapped — that's
// a safe default for spa vouchers, workshops, food kits, etc.
//
// NOTE: we deliberately don't export a `statusLabel(status, cat)`
// wrapper from here. The non-`baking` status maps differ per surface
// (customer-facing Orders uses "Out for delivery" while the vendor
// portal uses "Ready") so each page keeps its own map and only calls
// `preparingLabel` for the `baking` case.

/**
 * Returns the present-continuous verb a vendor uses when they're
 * actively working on the order. Drives:
 *
 *   - Customer-facing status badge on `/orders` and `/orders/:id`
 *   - Vendor portal status badge on `BakeryDashboard` / `BakeryOrders`
 *   - "Mark as ___" action button label on the vendor portal
 *   - Toast confirmation after a status transition
 *
 * The DB enum stays universal; this function only touches the display
 * layer.
 */
export function preparingLabel(vendorCategory?: string | null): string {
  switch (vendorCategory) {
    case "cake":
      return "Baking";
    case "hamper":
      return "Packing";
    case "florist":
    case "flowers":
      return "Tying";
    // MS278: coffee vendors issue digital vouchers; "Sending voucher"
    // reads more honestly than the default "Preparing" for vouchers,
    // since there's no physical preparation step.
    case "coffee":
      return "Sending voucher";
    default:
      return "Preparing";
  }
}
